import type { StorageAccessPolicy } from '../../../../../types/azureStorage';

/**
 * One editable stored-policy row (Fable F2). The row id is stable while the user edits, so the Identifier input is
 * never remounted by a keystroke (it used to be part of the React key). Loaded rows get positional ids — a refresh of
 * an unchanged draft keeps every input mounted — and added rows get fresh ones.
 */
export type AccessPolicyRow = { rowId: string; policy: StorageAccessPolicy };

let rowSequence = 0;

export const policyRowsFrom = (policies: StorageAccessPolicy[]): AccessPolicyRow[] => policies.map((policy, index) => ({ rowId: `loaded-${index}`, policy }));
export const policiesFromRows = (rows: AccessPolicyRow[]): StorageAccessPolicy[] => rows.map((row) => row.policy);

export function addAccessPolicyRow(rows: AccessPolicyRow[]): AccessPolicyRow[] {
  if (rows.length >= 5) return rows;
  let index = rows.length + 1;
  while (rows.some((row) => row.policy.id === `policy${index}`)) index += 1;
  return [...rows, { rowId: `added-${(rowSequence += 1)}`, policy: { id: `policy${index}` } }];
}

export function removeAccessPolicyRow(rows: AccessPolicyRow[], rowId: string): AccessPolicyRow[] {
  return rows.filter((row) => row.rowId !== rowId);
}

export function updateAccessPolicyRow(rows: AccessPolicyRow[], rowId: string, patch: Partial<StorageAccessPolicy>): AccessPolicyRow[] {
  return rows.map((row) => (row.rowId === rowId ? { ...row, policy: { ...row.policy, ...patch } } : row));
}

/**
 * What an edit of an EXISTING identifier widens for tokens already issued under it (Fable F7): letters added to a policy
 * that had letters, and an expiry moved later on a policy that had one. (Adding a field the policy lacked does not widen:
 * such tokens carry that field themselves, and Azure refuses a field set on both.)
 */
export function policyWidening(policy: StorageAccessPolicy, baseline: StorageAccessPolicy[]): { letters: string; expiry: boolean } | undefined {
  const before = baseline.find((candidate) => candidate.id === policy.id);
  if (!before) return undefined;
  const letters = before.permissions ? [...(policy.permissions ?? '')].filter((letter) => !before.permissions!.includes(letter)).join('') : '';
  const expiry = !!before.expiresOn && !!policy.expiresOn && Date.parse(policy.expiresOn) > Date.parse(before.expiresOn);
  return letters || expiry ? { letters, expiry } : undefined;
}
